import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Route, useRouteMatch, useHistory,
} from 'react-router-dom';

import { SubmissionError } from 'redux-form';
import formSteps from '../../../shared/helpers/formSteps';

import { anowApiPostRequest } from '../../../shared/libs/anowApiRequest';
import mapSubmitObject from '../../../shared/libs/mapSubmitObject';
import { getCurrentCompanyProfile, getFormConfig, initPaymentsDetails } from '../../actions/formConfigActions';

import { addOrderFormStep } from '../../actions/stepActions';
import ConfirmationScreen from '../../components/ConfirmationScreen/ConfirmationScreen';
import ErrorScreen from '../../components/ErrorScreen/ErrorScreen';
import PageLayout from '../../components/PageLayout/PageLayout';
import FifthScreen from '../../components/WizardForm/FifthScreen/FifthScreen';
import FirstScreen from '../../components/WizardForm/FirstScreen/FirstScreen';
import FourthScreen from '../../components/WizardForm/FourthScreen/FourthScreen';
import SecondScreen from '../../components/WizardForm/SecondScreen/SecondScreen';
import ThirdScreen from '../../components/WizardForm/ThirdScreen/ThirdScreen';

function exitPopup(e) {
  e.returnValue = ''; // Gecko, Trident, Chrome 34+
  return 'You have not submitted this order and if you leave your order will be lost.  Are you sure you want to leave this page?';
}

function OrderFormWrapper(props) {
  const { formConfig } = props;
  const match = useRouteMatch({
    path: '/:companyName',
    strict: true,
    sensitive: true,
  });
  const history = useHistory();
  useEffect(() => {
    props.getFormConfig(match?.params?.companyName);
    props.getCurrentCompanyProfile();
    if (window.isAuthenticated) {
      props.initPaymentsDetails();
    }
  }, []);
  if (!formConfig) {
    return <div />;
  }
  const {
    dueDateDefault,
    addressFields,
    detailsFields,
    assignmentFields,
    accessFields,
    attachmentFields,
    minDueDateDays,
    requireCoverage,
    turnAroundTimeMessage,
  } = formConfig;
  const {
    addOrderFormStep: addNextStep,
    companyDetails: {
      name, brandingColor, logoUrl, phoneNumbers, companyGuid,
    },
  } = props;
  return (
    <>
      <Route
        path="/:companyName"
        exact
      >
        <FirstScreen
          match={match}
          history={history}
          addNextStep={addNextStep}
          config={addressFields}
          companyName={name}
          brandingColor={brandingColor}
          logoUrl={logoUrl}
          requireCoverage={requireCoverage}
        />
      </Route>
      <Route
        path={[
          '/:companyName/details',
          '/:companyName/assignment',
          '/:companyName/access',
          '/:companyName/attachments',
        ]}
      >
        <PageLayout>
          <Route
            path="/:companyName/details"
            exact
          >
            <SecondScreen
              defaultDue={dueDateDefault}
              minDueDate={minDueDateDays}
              onSubmit={() => addNextStep(1 + 1)
                && history.push(
                  `/${match?.params?.companyName}${
                    formSteps[1 + 1]
                  }`,
                )}
              initialValues={
                dueDateDefault
                  ? {
                    dueDate: new Date(dueDateDefault),
                    fha: assignmentFields.fhaCaseNumber?.r
                      ? 'Yes'
                      : 'No',
                    appraisalPurpose: 'Purchase',
                  }
                  : { fha: 'No', appraisalPurpose: 'Purchase' }
              }
              keepDirtyOnReinitialize
              config={detailsFields}
              handleLeavePage={exitPopup}
              tatm={turnAroundTimeMessage}
            />
          </Route>
          <Route
            path="/:companyName/assignment"
            exact
          >
            <ThirdScreen
              keepDirtyOnReinitialize
              config={assignmentFields}
              onSubmit={() => addNextStep(4)
                && history.push(
                  `/${match?.params?.companyName}${
                    formSteps[4]
                  }`,
                )}
            />
          </Route>
          <Route
            path="/:companyName/access"
            exact
          >
            <FourthScreen
              config={accessFields}
              onSubmit={() => addNextStep(3)
                && history.push(
                  `/${match?.params?.companyName}${
                    formSteps[3]
                  }`,
                )}
            />
          </Route>
          <Route
            path="/:companyName/attachments"
            exact
          >
            <FifthScreen
              config={attachmentFields}
              onSubmit={(values) => anowApiPostRequest({
                url: `/appraisals/staged/${
                  window.isAuthenticated ? '' : 'anonymous'
                }`,
                body: JSON.stringify(mapSubmitObject(values)),
                companyHash: window.isAuthenticated
                  ? match?.params?.companyName
                  : companyGuid,
              })
                .then(
                  window.removeEventListener('beforeunload', exitPopup),
                )
                .then(({ data }) => {
                  if (Array.isArray(values.attachedFiles)) {
                    return anowApiPostRequest(
                      {
                        url: '/weborders/move/',
                        body: JSON.stringify(
                          {
                            path: `${data.companyGuid}/${data.stagedOrderGuid}`,
                            fileIds: values.attachedFiles.map(({ id }) => id),
                          },
                        ),
                        companyHash: match?.params?.companyName,
                      },
                      'POST',
                    ).then(() => history.push(
                      `/${match?.params?.companyName}/success`,
                    ));
                  }
                  history.push(
                    `/${match?.params?.companyName}/success`,
                  );
                  return true;
                })
                .catch((e) => {
                  history.push(`/${match?.params?.companyName}/error`);
                  Sentry.captureMessage(window.isAuthenticated ? 'Authenticated form' : 'Anonymous form', 'info');
                  Sentry.captureException(e);
                  throw new SubmissionError({ _error: e.message });
                })}
            />
          </Route>
        </PageLayout>
      </Route>
      <Route
        path="/:companyName/success"
        exact
      >
        <ConfirmationScreen match={match} />
      </Route>
      <Route
        path="/:companyName/error"
        exact
      >
        <ErrorScreen match={match} phone={phoneNumbers} />
      </Route>
    </>
  );
}

OrderFormWrapper.propTypes = {
  addOrderFormStep: PropTypes.func.isRequired,
  getFormConfig: PropTypes.func.isRequired,
  getCurrentCompanyProfile: PropTypes.func.isRequired,
  initPaymentsDetails: PropTypes.func.isRequired,
  // todo make real proptypes after endpoint will work
  // here's how https://transform.now.sh/
  // eslint-disable-next-line react/forbid-prop-types
  formConfig: PropTypes.object,
  companyDetails: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    addressTwo: PropTypes.string,
    city: PropTypes.string,
    province: PropTypes.string,
    country: PropTypes.string,
    postalCode: PropTypes.string,
    phoneNumbers: PropTypes.string,
    brandingColor: PropTypes.string,
    logoUrl: PropTypes.string,
    companyGuid: PropTypes.string,
  }).isRequired,
};

OrderFormWrapper.defaultProps = {
  formConfig: null,
};

const mapStateToProps = (state) => ({
  formConfig: state.formConfig,
  companyDetails: state.companyDetails,
});
export default connect(
  mapStateToProps,
  {
    addOrderFormStep,
    getFormConfig,
    getCurrentCompanyProfile,
    initPaymentsDetails,
  },
)(OrderFormWrapper);
